import React from 'react';
const engineFeatures = [{
  id: 1,
  title: 'Multi-Source Fusion',
  description: 'We do not rely on a single map. Navi fuses various data sources — crime data, municipal GIS, satellite imagery — into one high-resolution pedestrian graph.',
  accentColor: '#60a5fa',
  borderClass: 'border-blue-400'
}, {
  id: 2,
  title: 'Custom Route Costing',
  description: 'Standard maps optimize for the shortest path. Navi evaluates the best path by scoring each segment in real time for safety, lighting, and terrain.',
  accentColor: '#a78bfa',
  borderClass: 'border-violet-400'
}, {
  id: 3,
  title: 'Generative Routing',
  description: 'Purpose-built for people on foot. Multi-variable optimization solves for the route worth taking, not just the fastest one.',
  accentColor: '#34d399',
  borderClass: 'border-emerald-400'
}];
export function DataDepthSlide() {
  return <section className="slide" style={{
    backgroundColor: '#0d1520'
  }} data-id="element-199">
      <div className="flex flex-row w-full h-full min-h-0 relative overflow-hidden" data-id="element-200">
        {/* Left Content */}
        <div className="flex flex-col justify-center w-[50%] flex-shrink-0 min-h-0 overflow-hidden relative z-10" style={{
        padding: 'var(--slide-padding)',
        paddingLeft: 'clamp(2rem, 6vw, 6rem)'
      }} data-id="element-201">
          {/* Blue italic label */}
          <p className="italic font-semibold animate-fade-slide" style={{
          color: '#4A90D9',
          fontSize: 'var(--body-size)',
          marginBottom: 'clamp(0.5rem, 1.5vw, 1rem)',
          animationDelay: '0.1s'
        }} data-id="element-202">
            The engine.
          </p>

          {/* Big heading */}
          <h1 className="font-black tracking-tight leading-[0.95] text-white animate-fade-slide" style={{
          fontSize: 'var(--title-size)',
          marginBottom: 'clamp(1rem, 2.5vw, 2rem)',
          animationDelay: '0.15s'
        }} data-id="element-203">
            Beyond the shortest path.
          </h1>

          {/* Body paragraph */}
          <p className="leading-relaxed animate-fade-slide" style={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: 'var(--body-size)',
          maxWidth: 'clamp(280px, 32vw, 480px)',
          marginBottom: 'clamp(1.5rem, 3vw, 2.5rem)',
          animationDelay: '0.2s'
        }} data-id="element-204">
            Every map app optimizes for distance. Navi built a routing engine
            that scores every street segment for what actually matters when
            you're on foot — safety, scenery, shade, and vibe.
          </p>

          {/* Quote callout */}
          <div className="animate-fade-slide" style={{
          borderLeft: '3px solid #4A90D9',
          paddingLeft: 'clamp(0.75rem, 1.5vw, 1.25rem)',
          paddingTop: 'clamp(0.5rem, 1vw, 0.75rem)',
          paddingBottom: 'clamp(0.5rem, 1vw, 0.75rem)',
          animationDelay: '0.3s'
        }} data-id="element-205">
            <p className="font-bold text-white leading-snug" style={{
            fontSize: 'clamp(0.9rem, 1.6vw, 1.25rem)'
          }} data-id="element-206">
              Google gives you the best route for your car.
            </p>
            <p className="font-bold leading-snug" style={{
            fontSize: 'clamp(0.9rem, 1.6vw, 1.25rem)',
            color: '#4A90D9'
          }} data-id="element-207">
              Navi gives you the route you actually want to walk.
            </p>
          </div>
        </div>

        {/* Right Side — Engine feature cards */}
        <div className="flex-1 flex flex-col justify-center relative overflow-hidden" style={{
        padding: 'clamp(1.5rem, 3vw, 3rem) clamp(1.5rem, 3vw, 3rem)'
      }} data-id="element-208">
          {/* Section label */}
          <div className="flex items-center animate-fade-slide" style={{
          gap: 'clamp(0.5rem, 1vw, 1rem)',
          marginBottom: 'clamp(1rem, 2vw, 1.5rem)',
          animationDelay: '0.2s'
        }} data-id="element-209">
            <span className="uppercase tracking-[0.2em] font-semibold" style={{
            color: 'rgba(255,255,255,0.35)',
            fontSize: 'clamp(0.55rem, 0.8vw, 0.7rem)'
          }} data-id="element-210">
              How it works
            </span>
            <div className="flex-1" style={{
            height: '1px',
            backgroundColor: 'rgba(255,255,255,0.08)'
          }} data-id="element-211" />{' '}
          </div>

          {/* Feature Cards */}
          <div className="flex flex-col" style={{
          gap: 'clamp(0.75rem, 1.5vw, 1.25rem)'
        }} data-id="element-212">
            {engineFeatures.map((feature, i) => <div key={feature.id} className={`animate-fade-slide rounded-lg ${feature.borderClass} border-l-[3px]`} style={{
            backgroundColor: 'rgba(255,255,255,0.04)',
            padding: 'clamp(1rem, 2vw, 1.5rem) clamp(1rem, 2vw, 1.5rem)',
            animationDelay: `${0.3 + i * 0.1}s`
          }} data-id="element-213">
                {/* Feature number + title */}
                <div className="flex items-center" style={{
              gap: 'clamp(0.5rem, 1vw, 0.75rem)',
              marginBottom: 'clamp(0.3rem, 0.6vw, 0.5rem)'
            }} data-id="element-214">
                  <span className="font-mono flex-shrink-0" style={{
                color: feature.accentColor,
                fontSize: 'var(--small-size)',
                opacity: 0.7
              }} data-id="element-215">
                    0{feature.id}
                  </span>
                  <h3 className="font-bold text-white" style={{
                fontSize: 'clamp(1rem, 1.8vw, 1.35rem)'
              }} data-id="element-216">
                    {feature.title}
                  </h3>
                </div>

                {/* Description */}
                <p style={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: 'var(--body-size)',
              lineHeight: '1.5',
              paddingLeft: 'clamp(1.5rem, 2.5vw, 2.25rem)'
            }} data-id="element-217">
                  {feature.description}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}